import {Link} from "@tanstack/react-router";
import CalendarIcon from "@atlaskit/icon/core/calendar";
import FlagIcon from "@atlaskit/icon/core/flag";
import MilestoneCompleteIcon from "@atlaskit/icon-lab/core/milestone-complete";
import OnCallIcon from "@atlaskit/icon/core/on-call";
import PeopleGroupIcon from "@atlaskit/icon/core/people-group";
import PersonAddIcon from "@atlaskit/icon/core/person-add";
import TemplateIcon from "@atlaskit/icon-lab/core/template";
import type {ReactNode} from "react";
import {twJoin} from "tailwind-merge";
import {ScheduleCallIcon} from "~/components/svgs";
import {
    bottomNavIconBoxStyle,
    bottomNavScaffoldIconBoxStyle,
} from "~/components/pageScaffolds/workspacePageScaffold/bottomNav/bottomNavIconBoxStyles";
import {buildWorkspaceMobileBottomCreateHref} from "~/utilities/workspaceHelpers";
import type {
    WorkspaceMobileBottomCreateIconId,
    WorkspaceMobileBottomCreateItem,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceMobileBottomCreateItem";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

export function parseWorkspaceCreateHrefForRouter(href: string): {to: string; search?: Record<string, string>} {
    try {
        const url = new URL(href, window.location.origin);
        const searchEntries = Object.fromEntries(url.searchParams.entries());
        if (Object.keys(searchEntries).length > 0) {
            return {to: url.pathname, search: searchEntries};
        }
        return {to: url.pathname};
    } catch {
        return {to: href};
    }
}

export function workspaceCreateItemDescriptionForIconId(iconId: WorkspaceMobileBottomCreateIconId): string {
    switch (iconId) {
        case "leaderboard":
            return "Track user and team performance.";
        case "beatEvent":
            return "Manage beat plans and field activities.";
        case "inviteUser":
            return "Add new users to the workspace.";
        case "customers":
            return "Access customer profiles and journeys.";
        case "scheduleCall":
            return "Plan calls and reminders.";
        case "logCall":
            return "Record call interactions.";
        case "sales":
            return "Monitor sales activities and progress.";
        case "eventTemplate":
            return "Configure reusable event templates.";
        default: {
            const _exhaustive: never = iconId;
            return _exhaustive;
        }
    }
}

export function workspaceCreateIconForItem(iconId: WorkspaceMobileBottomCreateIconId): ReactNode {
    switch (iconId) {
        case "leaderboard":
            return <FlagIcon label="" color="currentColor" />;
        case "beatEvent":
            return <CalendarIcon label="" color="currentColor" />;
        case "inviteUser":
            return <PersonAddIcon label="" color="currentColor" />;
        case "customers":
            return <PeopleGroupIcon label="" color="currentColor" />;
        case "scheduleCall":
            return <ScheduleCallIcon />;
        case "logCall":
            return <OnCallIcon label="" color="currentColor" />;
        case "sales":
            return <MilestoneCompleteIcon label="" color="currentColor" />;
        case "eventTemplate":
            return <TemplateIcon label="" color="currentColor" />;
        default: {
            const _exhaustive: never = iconId;
            return _exhaustive;
        }
    }
}

function CreateFormGridItem({
    workspaceId,
    item,
    onNavigate,
    onBeforeNavigate,
}: {
    workspaceId: Uuid;
    item: WorkspaceMobileBottomCreateItem;
    onNavigate: () => void;
    onBeforeNavigate?: () => void;
}) {
    const href = buildWorkspaceMobileBottomCreateHref(workspaceId, item);
    const linkTarget = parseWorkspaceCreateHrefForRouter(href);
    return (
        <Link
            to={linkTarget.to}
            search={linkTarget.search}
            onClick={(event) => {
                const isModified =
                    event.metaKey === true ||
                    event.ctrlKey === true ||
                    event.shiftKey === true ||
                    event.altKey === true ||
                    event.button !== 0;
                if (isModified !== true) {
                    onBeforeNavigate?.();
                }
                onNavigate();
            }}
            className="flex w-full min-w-0 flex-col items-center gap-[10px] rounded-md p-1 hover:bg-ds-surface-hovered active:bg-ds-surface-pressed"
        >
            <div style={bottomNavIconBoxStyle}>
                <span className="grid h-full w-full place-items-center text-ds-icon-subtle [&_svg]:size-[24px]">
                    {workspaceCreateIconForItem(item.iconId)}
                </span>
            </div>
            <span className="w-full min-w-0 self-stretch break-words text-center text-[11px] font-normal leading-[1.25] text-ds-text-subtle">
                {item.displayName}
            </span>
        </Link>
    );
}

function CreateFormSidebarListItem({
    workspaceId,
    item,
    onNavigate,
    onBeforeNavigate,
}: {
    workspaceId: Uuid;
    item: WorkspaceMobileBottomCreateItem;
    onNavigate: () => void;
    onBeforeNavigate?: () => void;
}) {
    const href = buildWorkspaceMobileBottomCreateHref(workspaceId, item);
    const linkTarget = parseWorkspaceCreateHrefForRouter(href);
    return (
        <Link
            to={linkTarget.to}
            search={linkTarget.search}
            onClick={(event) => {
                const isModified =
                    event.metaKey === true ||
                    event.ctrlKey === true ||
                    event.shiftKey === true ||
                    event.altKey === true ||
                    event.button !== 0;
                if (isModified !== true) {
                    onBeforeNavigate?.();
                }
                onNavigate();
            }}
            className="grid w-full min-w-0 grid-cols-[max-content_minmax(0,1fr)] items-center gap-ds-space-150 rounded-md px-ds-space-100 py-ds-space-100 hover:bg-ds-surface-hovered active:bg-ds-surface-pressed"
        >
            <div style={bottomNavScaffoldIconBoxStyle}>
                <span className="grid h-full w-full place-items-center text-ds-icon-subtle [&_svg]:size-[20px]">
                    {workspaceCreateIconForItem(item.iconId)}
                </span>
            </div>
            <span className="min-w-0 text-[0.875rem] font-ds-font-weight-medium leading-ds-space-250 text-ds-text">
                {item.displayName}
            </span>
        </Link>
    );
}

/**
 * Shared grid of workspace create shortcuts (same cells as the mobile bottom-nav create sheet).
 * Used inside {@link BottomNavMobileCreateDrawer} and desktop list-card create side panel.
 */
export function WorkspaceCreateFormGrid({
    workspaceId,
    items,
    onNavigate,
    onBeforeNavigate,
    variant = "drawer",
}: {
    workspaceId: Uuid;
    items: readonly WorkspaceMobileBottomCreateItem[];
    onNavigate: () => void;
    onBeforeNavigate?: () => void;
    variant?: "drawer" | "dialog" | "sidebar";
}) {
    if (variant === "sidebar") {
        return (
            <div className="grid w-full gap-y-ds-space-050 px-ds-space-150 py-ds-space-150">
                {items.map((item) => (
                    <CreateFormSidebarListItem
                        key={item.id}
                        workspaceId={workspaceId}
                        item={item}
                        onNavigate={onNavigate}
                        onBeforeNavigate={onBeforeNavigate}
                    />
                ))}
            </div>
        );
    }

    const isDrawer = variant === "drawer";

    return (
        <div
            className={twJoin(
                "w-full pb-ds-space-200",
                isDrawer !== true &&
                    "grid gap-ds-space-150 content-start grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5",
            )}
            style={
                isDrawer === true
                    ? {
                          display: "grid",
                          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                          gap: "0.75rem",
                      }
                    : undefined
            }
        >
            {items.map((item) => (
                <CreateFormGridItem
                    key={item.id}
                    workspaceId={workspaceId}
                    item={item}
                    onNavigate={onNavigate}
                    onBeforeNavigate={onBeforeNavigate}
                />
            ))}
        </div>
    );
}
